import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { SearchOff } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlusOneIcon from "@mui/icons-material/PlusOne";
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TablePagination,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  getReferenceTable,
  getReferenceTables,
  reorderAlphabeticReferenceValues,
  reorderReferenceValues,
  toggleReferenceValue,
} from "../../api/reference";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { ReferenceTable as RefTableType } from "../../types/interfaces/admin";
import { ReferenceRow } from "../../types/model/reference";
import { getReferenceFields } from "../../utils";
import ConfirmationDialog from "../General/ConfirmationDialog";
import { ReferenceTableRow } from "./ReferenceTableRow";
import TableDialogForm from "./TableDialogForm";

interface Props {
  schema: string;
  table: string;
}

const ReferenceTable: React.FC<Props> = ({ schema, table }) => {
  const [tables, setTables] = useState<RefTableType[]>([]);
  const [fields, setFields] = useState<string[]>([]);
  const [data, setData] = useState<ReferenceRow[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ReferenceRow | null>(null);
  const [search, setSearch] = useState("");
  const [confirmAlphabeticReorderOpen, setConfirmAlphabeticReorderOpen] = useState(false);
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const navigate = useNavigate();

  const fetchTables = async () => {
    try {
      const tbs = await getReferenceTables();
      setTables(tbs);
      setFields(getReferenceFields(tbs, schema, table));
    } catch (error) {
      handleError(error);
    }
  };

  const fetchData = async () => {
    if (tables.length === 0) return;
    try {
      const rows = await getReferenceTable(tables, schema, table, true);
      setData(rows);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    if (tables.length > 0) {
      fetchData();
    }
  }, [tables, page, rowsPerPage]);

  // Toggle active/inactive
  const handleToggleActive = async (id: number) => {
    try {
      await toggleReferenceValue(tables, schema, table, id);
      fetchData();
    } catch (error) {
      handleError(error);
    }
  };

  // Drag & Drop reorder
  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const updatedData = Array.from(data);
    const [movedItem] = updatedData.splice(result.source.index, 1);
    updatedData.splice(result.destination.index, 0, movedItem);
    setData(updatedData);

    const ids = updatedData.map((row) => row.id);
    try {
      await reorderReferenceValues(tables, schema, table, ids);
    } catch (error) {
      handleError(error);
    }
  };

  // Alphabetic reorder
  const handleAlphabetic = () => {
    setConfirmAlphabeticReorderOpen(true);
  }

  const handleConfirmAlphabeticReorder = async () => {
    setConfirmAlphabeticReorderOpen(false);
    try {
      await reorderAlphabeticReferenceValues(tables, schema, table);
      fetchData();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <Paper sx={{ width: "100%", p: 2}}>
      <Box sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 5 }}>
          <Typography variant="h4" gutterBottom>
            {table}
          </Typography>

          <Tooltip title={t("generic.back")}>
            <Button
              onClick={() => navigate(-1)}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              <ArrowBackIcon />
            </Button>
          </Tooltip>
        </Box>

        <Toolbar>
          <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between", width: '100%' }}>
            <TextField
              label={t("generic.search")}
              variant="outlined"
              size="small"
              sx={{ maxWidth: 400 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Box>
              <Tooltip title={t("generic.order-alphabetic")}>
                <IconButton onClick={handleAlphabetic}>
                  <SortByAlphaIcon/>
                </IconButton>
              </Tooltip>

              <Tooltip title={t("generic.add")}>
                <IconButton onClick={
                  () => {
                    setEditingRow(null);
                    setDialogOpen(true);
                  }
                }>
                  <PlusOneIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Toolbar>

        {/* Table */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box component={Paper} sx={{ overflow: "hidden", boxShadow: 3 }}>
            {/* Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `40px repeat(${fields.length}, 1fr) 140px`,
                gap: 1,
                alignItems: "center",
                px: 3,
                py: 1,
                backgroundColor: "action.selected",
                fontWeight: "bold",
              }}
            >
              <Box /> 
              {fields.map((f) => (
                <Box key={f} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f}
                </Box>
              ))}
              <Box sx={{ textAlign: "right" }}>{t("admin.reference.table.row-actions")}</Box>
            </Box>

            {/* Rows */}
            <Droppable droppableId="referenceTable">
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ display: "block" }}>
                  {data.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 10,
                        width: "100%",
                      }}
                    >
                      <SearchOff sx={{ fontSize: 64, mb: 2, opacity: 0.6 }} />
                      <Typography variant="h6" sx={{ opacity: 0.6 }}>
                        {t("generic.no-data")}
                      </Typography>
                    </Box>
                  ) : (
                    data
                      .filter((row) =>
                        fields.some((f) =>
                          String((row as any)[f] || "").toLowerCase().includes(search.toLowerCase())
                        )
                      )
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, index) => (
                        <ReferenceTableRow
                          key={row.id}
                          row={row}
                          index={index}
                          fields={fields}
                          t={t}
                          onEdit={(row) => {
                            setEditingRow(row);
                            setDialogOpen(true);
                          }}
                          onToggleActive={handleToggleActive}
                        />
                      ))
                  )}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </Box>
        </DragDropContext>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={data.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ mt: 3 }}
        />

        {/* Dialog */}
        {dialogOpen && (
          <TableDialogForm
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            tables={tables}
            schema={schema}
            table={table}
            row={editingRow}
            fields={fields}
            onSaved={fetchData}
          />
        )}
        <ConfirmationDialog
          open={confirmAlphabeticReorderOpen}
          onCancel={() => setConfirmAlphabeticReorderOpen(false)}
          onConfirm={handleConfirmAlphabeticReorder}
          title={t("generic.order-alphabetic-confirm-title")}
          message={t("generic.order-alphabetic-confirm-text")}
        />
      </Box>
    </Paper>
  );
};

export default ReferenceTable;
